import { type SolarDesignDraftState } from './solarDesignDraft';
import {
  packFace,
  roofEdgeAnchorAzimuth,
  type FacePolygon,
  type LatLng,
  type PackedFacePanel,
} from './solarFacePacker';
import {
  type SolarGenerationDimensions,
  type SolarGenerationProvenance,
  unavailableSolarGeneration,
} from './solarGenerationAvailability';
import {
  physicalRoofPlane,
  resolveSolarMounting,
  type SolarMounting,
  type SolarRoofPlane,
} from './solarMounting';
import {
  recordRoofBearingRules,
  type SolarPlacementRules,
} from './solarPlacementRules';
import {
  roofEdgeAnchorIsValid,
  type SolarRoofEdgeAnchor,
} from './solarRoofEdgeAnchor';
import {
  hasMeasuredPlane,
  normaliseSolarPlaneFit,
  type SolarPlaneFit,
  type SolarPlaneFitInput,
} from './solarPlaneFit';
import { type SolarSavingsRegion } from './solarSavingsConstants';

/**
 * The one pack resolver, shared by the designer (Repack, Reset to automatic
 * layout, the first layout of an analysed design) and the API (Repack and
 * the first layout of a design Dave creates). The same faces, obstacles and
 * rules give the same panels by construction.
 */

type Layout = SolarDesignDraftState['layout'];

/** The fields of a working face the packer reads; designer and draft faces both fit. */
export type SolarPackFace = {
  id: number;
  polygon: FacePolygon;
  pitchDegrees: number;
  azimuthDegrees: number;
  segmentIndex: number;
  roofPlane?: SolarRoofPlane;
  mounting?: SolarMounting;
  arrayAzimuthDegrees?: number;
  roofEdgeAnchor?: SolarRoofEdgeAnchor | null;
  shading?: { annualSunAccessPercent: number } | null;
  generation?: SolarGenerationProvenance;
  /** "Include anyway": every repack packs this roof regardless of the skip rules. */
  rulesOverride?: true;
};

/** A packed panel as the working layout holds it. */
export type SolarPackedLayoutPanel = PackedFacePanel & {
  id: string;
  faceId: number;
  included: boolean;
};

export type SolarFacePackContext<F extends SolarPackFace = SolarPackFace> = {
  obstacles: LatLng[][];
  panelDims: { widthMeters: number; heightMeters: number };
  rules: SolarPlacementRules;
  region: SolarSavingsRegion;
  /** Indicative per-panel kWh for a face id, from the analysis' own panels. */
  energyForFace: (faceId: number) => number;
  /** The designer's live per-face estimate, preferred over energyForFace when it has one. */
  estimatePanelKwh?: (face: F) => number | null;
  /** Whether a panel's centre is on the property; true without a parcel. */
  includedByParcel: (corners: LatLng[]) => boolean;
  /** New panel identities; defaults to crypto.randomUUID(). */
  ids?: () => string;
};

export type SolarRepackContext<F extends SolarPackFace = SolarPackFace> =
  SolarFacePackContext<F> & {
    generationDimensions: SolarGenerationDimensions;
  };

/** Per-face array offsets keyed by face id (draft state keys are strings). */
type FaceOffsets = Readonly<Record<number, number>>;

const normaliseAzimuth = (degrees: number): number =>
  ((degrees % 360) + 360) % 360;

/**
 * A new analysed design starts from the Repack layout, not the analysis'
 * own panels. YourPower's vendor layout is the exception: it is kept as
 * supplied, and Repack applies the account's rules on request.
 */
export const solarAnalysedLayoutStartsFromRepack = (
  geometry: { roofModelProvider?: string | null } | null | undefined
): boolean => !!geometry && geometry.roofModelProvider !== 'YOURPOWER';

export const solarDesignRegistersToAnalysedImagery = (
  geometry: { roofModelProvider?: string | null } | null | undefined
): boolean => geometry?.roofModelProvider === 'GOOGLE';

/** Per-face mean of the analysis' annual kWh per panel, else the roof mean (0 without panels). */
export const solarEnergyForFaceFrom = (
  packedPanels:
    | ReadonlyArray<{ faceIndex: number; annualAcKwh: number }>
    | undefined
    | null
) => {
  const byFace = new Map<number, { sum: number; n: number }>();
  let total = 0;
  (packedPanels ?? []).forEach((panel) => {
    total += panel.annualAcKwh;
    const entry = byFace.get(panel.faceIndex) ?? { sum: 0, n: 0 };
    entry.sum += panel.annualAcKwh;
    entry.n += 1;
    byFace.set(panel.faceIndex, entry);
  });
  // A new roof has no historical output; never manufacture a 300 kWh default.
  const roofMean = packedPanels?.length ? total / packedPanels.length : 0;
  return (faceIndex: number) => {
    const entry = byFace.get(faceIndex);
    return entry && entry.n > 0 ? entry.sum / entry.n : roofMean;
  };
};

/** Ray-cast the panel centre against the title parcel; on-property without a parcel. */
export const solarPanelCentreOnProperty = (
  corners: LatLng[],
  parcelRing: LatLng[] | null | undefined
): boolean => {
  if (!parcelRing || parcelRing.length < 3 || corners.length === 0) return true;
  const lat = corners.reduce((sum, c) => sum + c.lat, 0) / corners.length;
  const lng = corners.reduce((sum, c) => sum + c.lng, 0) / corners.length;
  let inside = false;
  for (let i = 0, j = parcelRing.length - 1; i < parcelRing.length; j = i++) {
    const a = parcelRing[i];
    const b = parcelRing[j];
    if (
      a.lat > lat !== b.lat > lat &&
      lng < ((b.lng - a.lng) * (lat - a.lat)) / (b.lat - a.lat) + a.lng
    )
      inside = !inside;
  }
  return inside;
};

const arrayAzimuth = (
  face: SolarPackFace,
  polygon: FacePolygon,
  rules: SolarPlacementRules
): { roof: SolarRoofPlane; az: number } => {
  const roof = physicalRoofPlane(face);
  const az =
    (face.roofEdgeAnchor
      ? roofEdgeAnchorAzimuth(
          polygon,
          face.roofEdgeAnchor,
          roof.pitchDegrees,
          roof.azimuthDegrees,
          resolveSolarMounting(roof, face.mounting, rules).onFrames
        )
      : null) ??
    face.arrayAzimuthDegrees ??
    face.azimuthDegrees;
  return { roof, az };
};

/** The face as packed under `rules`: records applied mounting, rules and bearing. */
export const solarApplyFacePlacement = <F extends SolarPackFace>(
  face: F,
  rules: SolarPlacementRules,
  offsetDegrees: number,
  generationDimensions: SolarGenerationDimensions
): F => {
  // A legacy analysed draft may have lost the physical plane. Explicit pitch
  // confirmation is required; packing must not reconstruct one from tilt.
  if (!face.roofPlane) return face;
  const { roof: roofPlane, az } = arrayAzimuth(face, face.polygon, rules);
  const panel = resolveSolarMounting(
    roofPlane,
    face.mounting,
    rules,
    offsetDegrees,
    az
  );
  return {
    ...face,
    roofPlane,
    arrayAzimuthDegrees: face.arrayAzimuthDegrees ?? face.azimuthDegrees,
    appliedArrayAzimuthDegrees: face.roofEdgeAnchor
      ? normaliseAzimuth(az + offsetDegrees)
      : undefined,
    appliedMounting: panel.onFrames ? 'tilt' : 'flush',
    appliedPlacementRules: rules,
    appliedRoofEdgeAnchor: face.roofEdgeAnchor,
    mounting: face.mounting ?? { mode: 'auto' },
    pitchDegrees: panel.pitchDegrees,
    azimuthDegrees: panel.azimuthDegrees,
    generation:
      face.generation ??
      unavailableSolarGeneration({ ...face, ...panel }, generationDimensions),
  };
};

/**
 * An analysed face as a design first opens it: its plane fit normalised, and
 * placed under `rules` unless the roof was edited or has no measured angles.
 */
export const solarPlaceAnalysedFace = <
  F extends SolarPackFace & { planeFit?: SolarPlaneFitInput | null },
>(
  face: F,
  options: {
    edited: boolean;
    rules: SolarPlacementRules;
    generationDimensions: SolarGenerationDimensions;
  }
): Omit<F, 'planeFit'> & { planeFit?: SolarPlaneFit } => {
  const withPlaneFit = {
    ...face,
    planeFit: normaliseSolarPlaneFit(face.planeFit),
  };
  return !options.edited &&
    withPlaneFit.planeFit &&
    hasMeasuredPlane(withPlaneFit)
    ? solarApplyFacePlacement(
        withPlaneFit,
        options.rules,
        0,
        options.generationDimensions
      )
    : withPlaneFit;
};

/**
 * Packs one face (or `options.polygon`, a reshaped outline of it); null when
 * the face has no physical plane to pack against.
 */
export const solarPackFaceWithRules = <F extends SolarPackFace>(
  face: F,
  offsetDegrees: number,
  context: SolarFacePackContext<F>,
  options: {
    polygon?: FacePolygon;
    respectRoofSkips?: boolean;
    orientation?: PackedFacePanel['orientation'];
  } = {}
): SolarPackedLayoutPanel[] | null => {
  if (!face.roofPlane) return null;
  const { rules } = context;
  const polygon = options.polygon ?? face.polygon;
  const { roof, az } = arrayAzimuth(face, polygon, rules);
  const mounting = resolveSolarMounting(
    roof,
    face.mounting,
    rules,
    offsetDegrees,
    az
  );
  const nextId = context.ids ?? (() => crypto.randomUUID());
  return packFace({
    face: polygon,
    roofEdgeAnchor: face.roofEdgeAnchor,
    pitchDegrees: roof.pitchDegrees,
    rowAzimuthDegrees: az + offsetDegrees,
    mounting,
    obstructions: context.obstacles,
    panelWidthMeters: context.panelDims.widthMeters,
    panelHeightMeters: context.panelDims.heightMeters,
    faceIndex: face.id,
    segmentIndex: face.segmentIndex,
    energyPerPanelKwh:
      context.estimatePanelKwh?.({ ...face, ...mounting }) ??
      context.energyForFace(face.id),
    azimuthDegrees: roof.azimuthDegrees,
    sunAccessPercent: face.shading?.annualSunAccessPercent,
    rules,
    orientation: options.orientation,
    region: context.region,
    respectRoofSkips: (options.respectRoofSkips ?? true) && !face.rulesOverride,
  }).map((panel) => ({
    ...panel,
    id: nextId(),
    faceId: face.id,
    included: context.includedByParcel(panel.corners),
  }));
};

/** Whole-roof Repack refuses while any face lacks a plane or has an unusable edge anchor. */
export const solarRepackBlockedReason = (
  faces: readonly SolarPackFace[],
  rules: SolarPlacementRules
): string | null => {
  if (faces.some((face) => !face.roofPlane))
    return 'A roof has no confirmed pitch and bearing. Confirm it in the designer before repacking.';
  if (
    faces.some(
      (face) =>
        face.roofEdgeAnchor &&
        (!roofEdgeAnchorIsValid(face.roofEdgeAnchor, face.polygon.outer) ||
          face.roofEdgeAnchor.offsetM < rules.edgeSetbackM)
    )
  )
    return 'A roof edge anchor sits inside the edge setback. Adjust it in the designer before repacking.';
  return null;
};

export const solarWholeRoofRepackBlocked = (
  faces: readonly SolarPackFace[],
  rules: SolarPlacementRules
): boolean => solarRepackBlockedReason(faces, rules) != null;

/**
 * Repack every face ("Repack all roofs" / Reset / first analysed layout).
 * Null when whole-roof Repack would refuse, so callers keep what they have.
 */
export const solarPackAllFaces = <F extends SolarPackFace>({
  faces,
  offsets,
  context,
  respectRoofSkips = true,
}: {
  faces: readonly F[];
  offsets: FaceOffsets;
  context: SolarRepackContext<F>;
  respectRoofSkips?: boolean;
}): { faces: F[]; panels: SolarPackedLayoutPanel[] } | null => {
  if (solarWholeRoofRepackBlocked(faces, context.rules)) return null;
  return {
    panels: faces.flatMap(
      (face) =>
        solarPackFaceWithRules(face, offsets[face.id] ?? 0, context, {
          respectRoofSkips,
        }) ?? []
    ),
    faces: faces.map((face) =>
      solarApplyFacePlacement(
        face,
        context.rules,
        offsets[face.id] ?? 0,
        context.generationDimensions
      )
    ),
  };
};

/**
 * "Repack all roofs" on a draft state: every face re-laid with the design's
 * rules and pending mounting, panels replaced, roof-bearing provenance
 * recorded. Panel identities are new, as in the designer. Callers check
 * solarRepackBlockedReason first; a blocked roof leaves the state as it is.
 */
export const repackSolarDesignLayout = (
  state: SolarDesignDraftState,
  context: SolarRepackContext
): SolarDesignDraftState => {
  const { layout } = state;
  const packed = solarPackAllFaces({
    faces: layout.workingFaces,
    offsets: layout.faceOffsets,
    context,
  });
  if (!packed) return state;
  return {
    ...state,
    placementRules: context.rules,
    roofBearingRuleByFace: recordRoofBearingRules(
      packed.faces.map((face) => face.id),
      context.rules,
      state.roofBearingRuleByFace ?? {},
      true
    ),
    layout: {
      ...layout,
      workingFaces: packed.faces,
      workingPanels: packed.panels satisfies Layout['workingPanels'],
    },
  };
};
